import { useEffect, useState } from "react";
import { addProjectPaymentFile, deletePaymentFile, getProjectPaymentFiles } from "../../../Services/projectServices";
import toast from "react-hot-toast";
import { useParams } from "react-router-dom";
import { TOAST_MESSAGES } from "../constants";
import { CommonTable, FileUploader, Spinner } from "../../../Components/Common";
import { MdDownload } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import InfiniteScroll from "react-infinite-scroll-component";
import { downloadUtil } from "../../../Utils/DownloadUtil";

const RESET_PAGINATION = { offset: 0, limit: 5, count: 0 }; 

const PaymentFiles = ({ onClose, currentMilestoneStatus }) => {
  const [fileData, setFileData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [fileUploadLoading, setUploadLoading] = useState(false);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const { projectId } = useParams();

  const fetchFileData = async (
    offset = 0,
    limit = RESET_PAGINATION.limit,
    isInitial = false
  ) => {
    if (isInitial) setLoading(true);
    else setPaginationLoading(true);

    try {
      const response = await getProjectPaymentFiles(projectId, {
        offset,
        limit,
      });
      if (response.status === 200) {
        const newFiles = response.data.results || [];
        setFileData((prev) => (isInitial ? newFiles : [...prev, ...newFiles]));
        setPagination({
          offset: offset + limit,
          limit: RESET_PAGINATION.limit,
          count: response.data.count || 0,
        });
      } else {
        toast.error(TOAST_MESSAGES.FILES_FETCHED_ERROR);
      }
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILES_FETCHED_ERROR);
    } finally {
      if (isInitial) setLoading(false);
      else setPaginationLoading(false);
    }
  };

  useEffect(() => {
    fetchFileData(0, RESET_PAGINATION.limit, true);
  }, []);

  const loadMoreFiles = () => {
    if (pagination.offset >= pagination.count) return;
    fetchFileData(pagination.offset, RESET_PAGINATION.limit);
  };

  const handleFileChange = async (file) => {
    setUploadLoading(true);
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("project", projectId);

    try {
      const response = await addProjectPaymentFile(projectId, formData);
      if (response.status === 201) {
        toast.success(TOAST_MESSAGES.FILE_UPLOADED_SUCCESFULLY);
        fetchFileData(0, RESET_PAGINATION.limit, true); // refresh list from first page
      } else {
        toast.error(TOAST_MESSAGES.FILE_UPLOAD_FAILED);
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          err.message ||
          TOAST_MESSAGES.FILE_UPLOAD_FAILED
      );
    } finally {
      setUploadLoading(false);
    }
  };

  const handleDeleteFile = async (fid) => {
    setLoading(true);
    try {
      const results = await deletePaymentFile(projectId, fid);
      if (results.status === 200) {
        toast.success(TOAST_MESSAGES.FILE_DELETE_SUCESS);
        fetchFileData(0, RESET_PAGINATION.limit, true); // refresh list from first page
      } else {
        toast.error(TOAST_MESSAGES.FILE_DELETE_FAIL);
      }
    } catch {
      toast.error(TOAST_MESSAGES.FILE_DELETE_FAIL);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (fileUrl) => {
    try {
      await downloadUtil(fileUrl);
      toast.success(TOAST_MESSAGES.FILE_DOWNLOAD_SUCCESSFUL);
    } catch (error) {
      toast.error(TOAST_MESSAGES.FILE_DOWNLOAD_FAIL);
    }
  };

  const handleRenderField = (key, rowData) => {
    if (key === "Actions") {
      return (
        <div key={rowData.id} className="flex space-x-2">
          <div className="flex justify-between gap-4">
            <button
              onClick={() => handleDownload(rowData.file)}
              title="Download File"
            >
              <MdDownload
                size={20}
                className="text-secondary hover:text-blue-600"
              />
            </button>
            <button
              onClick={() => handleDeleteFile(rowData.id)}
              disabled={currentMilestoneStatus === "COMPLETED"}
              title={`${
                currentMilestoneStatus === "COMPLETED"
                  ? "Can not delete after project completed"
                  : "Delete file"
              }`}
              className={`${
                currentMilestoneStatus === "COMPLETED"
                  ? "text-gray-400 cursor-not-allowed"
                  : "text-red-500 hover:text-blue-600"
              }`}
            >
              <FiTrash2
                size={20}
              />
            </button>
          </div>
        </div>
      );
    }

    if (key === "File") {
      const fileName = rowData.file?.split("/").pop().split("?")[0];
      return (
        <a
          href={rowData.file}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
          title={fileName}
        >
          {fileName || "N/A"}
        </a>
      );
    }

    return rowData[key] || "N/A";
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center py-3 px-4 border-b">
          <div className="flex justify-between items-center gap-4">
            <h2 className="text-md font-semibold">Payment Files</h2>
            <FileUploader
              onUploadClick={handleFileChange}
              isUploading={fileUploadLoading}
              singleButton={true}
              singleBtnClassName="py-1 px-4 border rounded-md hover:bg-gray-100 hover:text-gray-700 text-gray-700"
            />
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800 text-xl"
          >
            ✕
          </button>
        </div>

        {/* File List */}
        <div
          id="paymentFileScrollDiv"
          className="h-60 overflow-x-hidden overflow-y-scroll thin-scrollbar p-4"
        >
          {loading ? (
            <div className="flex justify-center items-center">
              <Spinner />
            </div>
          ) : (
            <InfiniteScroll
              dataLength={fileData.length}
              next={loadMoreFiles}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="paymentFileScrollDiv"
              loader={
                paginationLoading && (
                  <div className="flex justify-center py-2">
                    <Spinner size={3} />
                  </div>
                )
              }
            >
              <CommonTable
                tableColumns={["File", "Actions"]}
                tableRows={fileData}
                renderField={handleRenderField}
                className={"border-2 shadow-md text-md"}
              />
            </InfiniteScroll>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentFiles;
